/**
 * The learner's mother tongue (D-107): chosen at sign-up and in the preferences. The list comes
 * from the server (versioned data); a « beta » language says clearly that the AI translation is
 * uncertain, and a language without a voice says it will be read in French or English.
 */

import { useAcademyLanguagesList, useAcademyMePartialUpdate, useAcademyMeRetrieve } from "@agencyos/api-client";
import { type SpokenLanguage, languageLabel, parseLanguages } from "@agencyos/core";
import { GlassCard, SelectField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function useLanguages(): SpokenLanguage[] {
  const query = useAcademyLanguagesList({ query: { staleTime: 3_600_000, retry: false } });
  return useMemo(() => parseLanguages(query.data?.status === 200 ? query.data.data : []), [query.data]);
}

/** The honest note under a language: beta translation, no voice. */
export function LanguageNote({ language }: { language: SpokenLanguage | undefined }) {
  const { t } = useTranslation();
  if (!language) return null;
  const notes = [
    language.beta ? t("academyCourse.tongue.beta") : "",
    !language.serverVoice && !language.deviceLocale ? t("academyCourse.tongue.noVoice") : "",
  ].filter(Boolean);
  if (!notes.length) return null;
  return (
    <p className="ac-footnote ac-tongue-note" role="note">
      {notes.join(" ")}
    </p>
  );
}

export function MotherTongueSelect({ value, onChange, label }: { value: string; onChange: (code: string) => void; label?: string }) {
  const { t, i18n } = useTranslation();
  const languages = useLanguages();
  const current = languages.find((l) => l.code === value);
  return (
    <div className="ac-tongue">
      <SelectField
        label={label ?? t("academyCourse.tongue.label")}
        hint={t("academyCourse.tongue.hint")}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        options={[{ value: "", label: t("academyCourse.tongue.none") }, ...languages.map((l) => ({ value: l.code, label: languageLabel(l, i18n.language) }))]}
      />
      <LanguageNote language={current} />
    </div>
  );
}

/** In the preferences: the mother tongue saved on the learner. */
export function MotherTonguePreference() {
  const queryClient = useQueryClient();
  const me = useAcademyMeRetrieve({ query: { retry: false, staleTime: 60_000 } });
  const save = useAcademyMePartialUpdate({ mutation: { onSuccess: () => void queryClient.invalidateQueries() } });
  const value = me.data?.status === 200 ? (me.data.data.mother_tongue ?? "") : "";
  if (me.data?.status !== 200) return null;
  return <MotherTongueSelect value={value} onChange={(code) => save.mutate({ data: { mother_tongue: code } })} />;
}

/** At the top of the course: asks the mother tongue while it is not known, so the explanations are
 * translated and read in the learner's language (D-107). Nothing once it is chosen. */
export function MotherTonguePrompt() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useAcademyMeRetrieve({ query: { retry: false, staleTime: 60_000 } });
  const save = useAcademyMePartialUpdate({ mutation: { onSuccess: () => void queryClient.invalidateQueries() } });
  if (me.data?.status !== 200 || me.data.data.mother_tongue) return null;
  return (
    <GlassCard as="section" className="ac-card ac-tongue-prompt" aria-labelledby="ac-tongue-prompt">
      <h2 id="ac-tongue-prompt" className="ac-tongue-prompt__title">
        {t("academyCourse.tongue.promptTitle")}
      </h2>
      <p className="ac-tongue-prompt__text">{t("academyCourse.tongue.promptText")}</p>
      <MotherTongueSelect value="" onChange={(code) => code && save.mutate({ data: { mother_tongue: code } })} />
    </GlassCard>
  );
}
